import { Checkbox, FormControlLabel, Grid, MenuItem, Select, Typography } from '@material-ui/core';
import React, { useContext, useState } from 'react';

import { GenerateFields } from './CheckoutInput';
import {
  CheckoutContext,
  Environments,
  FlowsOptions,
  ICheckoutState,
  PaymentOptions,
  SourceOptions,
} from '../../store/CheckoutContext';
import { setEnvironment } from '../../helpers/environment-helper';
import { validate as isValidUUID } from 'uuid';
import { resultsList } from '../../data/results-list';
import { useLocation } from 'react-router-dom';

import './Checkout.css';
import { currencies } from '../../data/currencies';
import { checkIfValidUUID } from '../../helpers/checkout-helpers';

interface Props {
  children: React.ReactChild;
  state: ICheckoutState;
  setState: React.Dispatch<React.SetStateAction<{ [key: string]: any }>>;
}

const environments = ['local', 'development', 'staging', 'sandbox', 'production'];
const redirects = [
  {
    value: 'modal',
    label: 'Show as modal',
  },
  {
    value: 'same_page',
    label: 'Redirect to the same tab',
  },
  {
    value: 'new_tab',
    label: 'Redirect to new tab',
  },
];

const iframeSizes = [
  {
    value: '01',
    label: '01',
  },
  {
    value: '02',
    label: '02',
  },
  {
    value: '03',
    label: '03',
  },
  {
    value: '04',
    label: 'fullscreen',
  },
];

const paymentOptionValues = [
  {
    value: PaymentOptions.cit,
    label: 'CIT - Customer-initiated transaction',
  },
  {
    value: PaymentOptions.mit,
    label: 'MIT - Merchant-initiated transaction',
  },
];

const sourceOptionsValues = [
  {
    value: SourceOptions.empty,
    label: 'Empty',
  },
  {
    value: SourceOptions.mailorder,
    label: SourceOptions.mailorder,
  },
  {
    value: SourceOptions.installment,
    label: SourceOptions.installment,
  },
  {
    value: SourceOptions.ecommerce,
    label: SourceOptions.ecommerce,
  },
  {
    value: SourceOptions.applepay,
    label: SourceOptions.applepay,
  },
  {
    value: SourceOptions.androidpay,
    label: SourceOptions.androidpay,
  },
  {
    value: SourceOptions.recurring,
    label: SourceOptions.recurring,
  },
  {
    value: SourceOptions.retail,
    label: SourceOptions.retail,
  },
  {
    value: SourceOptions.telephone,
    label: SourceOptions.telephone,
  },
  {
    value: SourceOptions.vterminal,
    label: SourceOptions.vterminal,
  },
  {
    value: SourceOptions.terminal,
    label: SourceOptions.terminal,
  },
];

export const flowsOptionsValues = [
  {
    value: FlowsOptions.iframe,
    label: 'Iframe',
  },
  {
    value: FlowsOptions.terminal,
    label: 'Terminal',
  },
  {
    value: FlowsOptions.steps,
    label: 'Steps',
  },
  {
    value: FlowsOptions.withoutOrderId,
    label: 'Without setOrderId',
  },
];

const CheckoutForm: React.FC<Props> = props => {
  const { state, children, setState } = props;
  const {
    environment,
    setEnvironment: setStoreEnvironment,
    errors,
    redirect,
    setRedirect,
    iframeSize,
    setIframeSize,
    paymentOptions,
    setPaymentOptions,
    sourceOptions,
    setSourceOptions,
  } = useContext(CheckoutContext);
  const [showAdditionalFields, setAdditionalFields] = useState(false);
  const [localEnvironment, setLocalEnvironment] = useState(environment);
  const [mid] = useState(state.mid);
  const [siteId] = useState(state.siteId);
  const location = useLocation();

  function handleEnvironmentChange(newEnvironment: Environments) {
    setStoreEnvironment(newEnvironment);
    setLocalEnvironment(newEnvironment);
    setEnvironment(newEnvironment, Number(newEnvironment), true);
  }

  // function handleBlur(e: any, name: string) {
  //   if (name === 'mid' && e.target.value !== mid && isValidUUID(e.target.value)) {
  //     handleEnvironmentChange(environment as Environments);
  //     localStorage.setItem('mid', e.target.value);
  //   }
  //
  //   if (name === 'siteId' && e.target.value !== mid && isValidUUID(e.target.value)) {
  //     handleEnvironmentChange(environment as Environments);
  //     localStorage.setItem('siteId', e.target.value);
  //   }
  // }

  function handleBlur(e: any, name: string) {
    if (name === 'mid' && e.target.value !== mid && checkIfValidUUID(e.target.value)) {
      handleEnvironmentChange(environment as Environments);
      localStorage.setItem('mid', e.target.value);
    }

    if (name === 'siteId' && e.target.value !== mid && checkIfValidUUID(e.target.value)) {
      handleEnvironmentChange(environment as Environments);
      localStorage.setItem('siteId', e.target.value);
    }
  }

  function showDevFields() {
    return Object.keys(errors).some(el => ['appKey', 'appSecret', 'spreedlyKey', 'mid'].includes(el));
  }

  return (
    <>
      <Grid item>
        <div className="payment">
          <Typography variant="h6" gutterBottom>
            Payment method
          </Typography>
          {children}
        </div>
      </Grid>
      <Grid item>
        <div className="shipping">
          <Typography variant="h6" gutterBottom>
            Billing address
          </Typography>
          <Grid container spacing={3}>
            <GenerateFields state={state} setState={setState} fieldName={'address'} />

            {!location.pathname.includes('demo') && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="secondary"
                      name="saveAddress"
                      value={!!state?.saveAddress}
                      onChange={e => setState({ ...state, saveAddress: !state?.saveAddress })}
                      data-cy="save-address-checkbox"
                    />
                  }
                  label="Use this address for shipment details"
                />
              </Grid>
            )}

            {!location.pathname.includes('demo') && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="secondary"
                      name="additionalFields"
                      value={showAdditionalFields}
                      onChange={() => setAdditionalFields(!showAdditionalFields)}
                    />
                  }
                  label="Show developer fields"
                />
              </Grid>
            )}
          </Grid>
        </div>
      </Grid>
      {(showAdditionalFields || showDevFields()) && (
        <Grid container spacing={5} className="">
          <Grid item>
            <div className="shipping">
              <Grid item>
                <Typography variant="h6" gutterBottom>
                  Developers
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <label className="checkout-error">
                      <div>Environment</div>
                      <Select
                        className="input"
                        labelId="sizeLabel"
                        value={localEnvironment}
                        onChange={event => handleEnvironmentChange(event.target.value as Environments)}
                      >
                        {environments.map(item => (
                          <MenuItem key={item} value={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                    </label>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <label className="checkout-error">
                      <div>Iframe redirect</div>
                      <Select
                        className="input"
                        labelId="sizeLabel"
                        value={redirect}
                        onChange={event => setRedirect(event.target.value as string)}
                      >
                        {redirects.map(item => (
                          <MenuItem key={item.value} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </label>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <label className="checkout-error">
                      <div>Iframe redirect</div>
                      <Select
                        className="input"
                        labelId="sizeLabel"
                        value={iframeSize}
                        onChange={event => setIframeSize(event.target.value as string)}
                      >
                        {iframeSizes.map(item => (
                          <MenuItem key={item.value} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </label>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <label className="checkout-error">
                      <div>Transaction options</div>
                      <Select
                        className="input"
                        labelId="paymentOption"
                        value={paymentOptions}
                        onChange={event => setPaymentOptions(event.target.value as PaymentOptions)}
                      >
                        {paymentOptionValues.map(item => (
                          <MenuItem key={item.value} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </label>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <label className="checkout-error">
                      <div>Order Source</div>
                      <Select
                        className="input"
                        labelId="OrderSource"
                        value={sourceOptions}
                        onChange={event => setSourceOptions(event.target.value as SourceOptions)}
                      >
                        {sourceOptionsValues.map(item => (
                          <MenuItem key={item.value} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </label>
                  </Grid>

                  {/*<Grid item xs={12} sm={6}>*/}
                  <GenerateFields state={state} setState={setState} fieldName={'dev'} />
                  {/*</Grid>*/}

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="secondary"
                          name="closeLoader"
                          value={!!state?.closeLoaderCallback}
                          checked={!!state?.closeLoaderCallback}
                          onChange={e => setState({ ...state, closeLoaderCallback: !state?.closeLoaderCallback })}
                          data-cy="closeLoader"
                        />
                      }
                      label="Use only closeLoader callback"
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          color="secondary"
                          name="useSale"
                          value={!!state?.useSale}
                          checked={!!state?.useSale}
                          onChange={e => setState({ ...state, useSale: !state?.useSale })}
                          data-cy="useSale"
                        />
                      }
                      label="Use Sale"
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          color="secondary"
                          name="isAsynchronous"
                          value={!!state?.isAsynchronous}
                          checked={!!state?.isAsynchronous}
                          onChange={e => setState({ ...state, isAsynchronous: !state?.isAsynchronous })}
                          data-cy="isAsynchronous"
                        />
                      }
                      label="Is Asynchronous"
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          color="secondary"
                          name="withEvaluate"
                          value={!!state?.withEvaluate}
                          checked={!!state?.withEvaluate}
                          onChange={e => setState({ ...state, withEvaluate: !state?.withEvaluate })}
                          data-cy="withEvaluate"
                        />
                      }
                      label="With evaluate"
                    />
                  </Grid>

                  <Grid item>
                    <div className="payment payment-container">
                      <Typography variant="h6" gutterBottom>
                        Subscription Data
                      </Typography>
                      <Grid container spacing={3}>
                        <GenerateFields state={state} setState={setState} fieldName={'development'} />
                      </Grid>
                    </div>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="secondary"
                          name="openLoader"
                          value={!!state?.openLoaderCallback && !!state?.closeLoaderCallback}
                          checked={!!state?.openLoaderCallback && !!state?.closeLoaderCallback}
                          onChange={e =>
                            setState({
                              ...state,
                              closeLoaderCallback: !state?.openLoaderCallback,
                              openLoaderCallback: !state?.openLoaderCallback,
                            })
                          }
                          data-cy="openLoader"
                        />
                      }
                      label="Use closeLoader and openLoader callbacks"
                    />
                  </Grid>
                </Grid>
                {/*<Typography variant="h6" gutterBottom style={{ marginTop: '15px' }}>*/}
                {/*  Payee*/}
                {/*</Typography>*/}
                {/*<Grid container spacing={3}>*/}
                {/*  <GenerateFields state={state} setState={setState} fieldName={'payee'} />*/}
                {/*  <Grid item xs={12}>*/}
                {/*    <FormControlLabel*/}
                {/*      control={*/}
                {/*        <Checkbox*/}
                {/*          id="flex-charge-terms"*/}
                {/*          color="secondary"*/}
                {/*          name="flexCharge"*/}
                {/*          checked={!!state?.flexChargeTerms}*/}
                {/*          onChange={e => setState({ ...state, flexChargeTerms: !state?.flexChargeTerms })}*/}
                {/*        />*/}
                {/*      }*/}
                {/*      label={*/}
                {/*        <span>*/}
                {/*          If this payment transaction fails, I consent to FlexCharge processing my request for a free*/}
                {/*          credit facility (no interest, no fees) to complete this purchase now and be charged later,*/}
                {/*          accordance with the{' '}*/}
                {/*          <a href="src/views/checkout/Iframe" target="_blank" rel="noreferrer">*/}
                {/*            Terms and Conditions*/}
                {/*          </a>*/}
                {/*          ,{' '}*/}
                {/*          <a href="src/views/checkout/Iframe" target="_blank" rel="noreferrer">*/}
                {/*            User Terms and Privacy Notice of FlexCharge*/}
                {/*          </a>*/}
                {/*          , a trusted Pet Shop partner.*/}
                {/*        </span>*/}
                {/*      }*/}
                {/*    />*/}
                {/*  </Grid>*/}
                {/*</Grid>*/}
                <Typography variant="h6" gutterBottom style={{ marginTop: '15px' }}>
                  Identification
                </Typography>
                <Grid container spacing={3}>
                  <GenerateFields
                    state={state}
                    setState={setState}
                    fieldName={'identification'}
                    handleBlur={handleBlur}
                  />
                </Grid>
              </Grid>
            </div>
          </Grid>
          <Grid item>
            <div className="payment payment-container">
              <Typography variant="h6" gutterBottom>
                Transaction
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <label className="checkout-error">
                    <div>Response code</div>
                    <Select
                      className="input"
                      labelId="responseCode"
                      value={state.responseCode}
                      onChange={event => setState({ ...state, responseCode: event.target.value })}
                    >
                      {resultsList.map(item => (
                        <MenuItem key={item.description} value={item.code}>
                          {`${item.code} ${item.description}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </label>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <label className="checkout-error">
                    <div>Currency</div>
                    <Select
                      className="input"
                      labelId="currency"
                      value={state.currency}
                      onChange={event => setState({ ...state, currency: event.target.value })}
                    >
                      {currencies.map(item => (
                        <MenuItem key={item} value={item}>
                          {item}
                        </MenuItem>
                      ))}
                    </Select>
                  </label>
                </Grid>

                <GenerateFields state={state} setState={setState} fieldName={'transaction'} />
              </Grid>
            </div>
          </Grid>
          <Grid item>
            <div className="payment payment-container">
              <Typography variant="h6" gutterBottom>
                Redirect urls
              </Typography>
              <Grid container spacing={3}>
                <GenerateFields state={state} setState={setState} fieldName={'urls'} />
              </Grid>
            </div>
          </Grid>

          <Grid item>
            <div className="payment payment-container">
              <Typography variant="h6" gutterBottom>
                Evaluate payload
              </Typography>
              <Grid container spacing={3}>
                <GenerateFields state={state} setState={setState} fieldName={'evaluate'} />
              </Grid>
            </div>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default CheckoutForm;
